import { Controller } from "@hotwired/stimulus"

// Cook this recipe for a different number of people.
//
// "Servings: 4" rather than "x2", because servings is the number you actually
// have in your head — you are cooking for four, not multiplying by two. The
// scale is derived from it.
//
// Every quantity is re-rendered by the SERVER, because scaling a quantity is
// not a multiply: "1½ tsp" doubled reads "3 tsp", "¾ onion" doubled reads
// "1½ onions", one slice becomes two slices, and past four of something
// countable the fractions stop helping and it rounds to whole items.
// FractionalQuantities knows all of that and is worth exactly one
// implementation.
//
// Deliberately not persisted: a reading aid, not a property of the recipe.
// Reload and you are back to the recipe as written.
export default class extends Controller {
  static targets = [ "field", "quantity" ]

  connect() {
    this.servings = this.baseValue
    this.pending = null
  }

  // THE SAME LADDER THE STEPPERS USE, passed in from SERVING_LADDER so there
  // is one answer to "what is a sensible number of servings" rather than
  // three controls each with an opinion.
  //
  // This had its own increments — halves below two, whole numbers above —
  // which was a reasonable rule and could not reach a third or a quarter.
  // The planner and the day view have stepped to those since the ladder
  // landed, so scaling a recipe was the one place that could not.
  //
  // ONE DECLARATION. There were two — `{ url, base }` up beside the targets
  // and this one — and the second silently REPLACED the first, because a
  // duplicate static property assignment overwrites rather than merges and
  // JavaScript says nothing about it.
  //
  // So urlValue and baseValue were undefined. connect() set this.servings
  // to undefined, the first step computed undefined + 1 = NaN, and the
  // field went blank; the fetch would have gone to "undefined?servings=".
  // Nothing threw, and the endpoint it could not reach was working fine.
  static values = {
    url: String,
    base: Number,
    ladder: { type: Array, default: [] }
  }

  // NO STEPPING LOGIC OF ITS OWN. stepFrom, nextValue and step went with
  // the buttons they served: shared/_stepper_controls owns the arrows now
  // and walks SERVING_LADDER itself, then writes the result to the hidden
  // field below. This controller hears the `input` that follows.
  //
  // What is left is the half nobody else can do — deciding a value is
  // usable, and asking the server to re-render every quantity for it.
  typed() {
    this.request(Number(this.fieldTarget.value))
  }

  // One place decides what a valid serving count is, and it is the only
  // place that talks to the server.
  request(servings) {
    // NO ROUNDING OF ITS OWN ANY MORE. This used to snap to halves below 2
    // and whole numbers above, because the buttons could only produce those
    // — and once the ladder arrived that became a second opinion that
    // overrode it. Measured against SERVING_LADDER: a quarter and a third
    // were refused outright, two thirds silently became a half and three
    // quarters became one. Four of the five rungs below a serving were
    // unreachable while the control drew them.
    //
    // The ladder is the answer to "what is a sensible number of servings",
    // which is the whole reason it was passed in. A typed value is exact
    // and stands as typed, the same as everywhere else a stepper is used.
    const wanted = servings

    // THE FLOOR IS THE LADDER'S OWN FIRST RUNG rather than a separate
    // number. It said 0.5 here and 0.25 there, and the two disagreeing is
    // what made the quarter unreachable. Scaling a recipe to a quarter is a
    // real thing — one portion of something written for four — and the
    // planner and Today already allow it.
    //
    // The ceiling guards against a typo becoming a very slow render rather
    // than stating a real limit.
    const floor = this.ladderValue.length ? this.ladderValue[0] : 0.5
    if (!Number.isFinite(wanted) || wanted < floor || wanted > 100) {
      this.fieldTarget.value = this.servings
      return
    }

    this.fieldTarget.value = wanted
    if (wanted === this.servings) return

    this.servings = wanted
    this.fetchQuantities(wanted)
  }

  async fetchQuantities(servings) {
    // The last request wins. Holding + steps up faster than the round trip,
    // so without this an earlier, smaller response can land after a later one
    // and leave the quantities disagreeing with the number in the box.
    const token = {}
    this.pending = token

    this.element.classList.add("is-scaling")

    try {
      const response = await fetch(
        `${this.urlValue}?servings=${encodeURIComponent(servings)}`,
        { headers: { Accept: "application/json" } }
      )
      if (!response.ok || this.pending !== token) return

      const body = await response.json()
      if (this.pending !== token) return

      for (const element of this.quantityTargets) {
        const value = body.quantities[element.dataset.scalerKey]
        if (value !== undefined) element.textContent = value
      }
    } catch {
      // A failed request leaves the quantities as they were, which is the
      // honest thing to show — they still match the last number that loaded.
      this.fieldTarget.value = this.servings
    } finally {
      if (this.pending === token) {
        this.pending = null
        this.element.classList.remove("is-scaling")
      }
    }
  }
}
